import React from "react";
import {
  House,
  Fingerprint,
  History,
  TrendingUp,
  ChartNoAxesCombined,
  Share2,
  Images,
  LayoutGrid,
  BookOpen,
  Palette,
  PenTool,
  Layers,
  FolderOpen,
  Shapes,
  Grid2x2,
  Image,
  PenLine,
  Aperture,
  Camera,
  GalleryHorizontalEnd,
  Shirt,
  Mountain,
  PersonStanding,
  FileText,
  Sparkles,
  SwatchBook,
  PencilRuler,
  type LucideIcon,
} from "lucide-react";

/**
 * Left-menu icons, sourced from Lucide (`lucide-react`, already a project dependency). Lucide is a
 * stroke-only (outline) set: icons render `fill="none"` and colour via `currentColor`, so they inherit
 * the row's text colour (theme-aware, dim with the row). Sizing is left to the caller's `className`
 * (the menu tree sets 20px top-level / 16px nested), so we don't also pass `size` — double-controlling
 * it renders the strokes oddly. `strokeWidth` is in the 24-unit viewBox, so it scales with the icon.
 */
type MenuIconProps = { className?: string };

const makeMenuIcon = (Glyph: LucideIcon): React.FC<MenuIconProps> => {
  const MenuIcon = ({ className }: MenuIconProps) => <Glyph strokeWidth={2} className={className} />;

  return MenuIcon;
};

const MenuModel = ({ className }: MenuIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M10 16v5" />
    <path d="M14 16v5" />
    <path d="M8 16h8l-2 -7h-4l-2 7" />
    <path d="M5 11c1.667 -1.333 3.333 -2 5 -2" />
    <path d="M19 11c-1.667 -1.333 -3.333 -2 -5 -2" />
    <path d="M10 4a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
  </svg>
);

const MenuIntegrations = ({ className }: MenuIconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M4 7h3a1 1 0 0 0 1 -1v-1a2 2 0 0 1 4 0v1a1 1 0 0 0 1 1h3a1 1 0 0 1 1 1v3a1 1 0 0 0 1 1h1a2 2 0 0 1 0 4h-1a1 1 0 0 0 -1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-1a2 2 0 0 0 -4 0v1a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h1a2 2 0 0 0 0 -4h-1a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1" />
  </svg>
);

export const MenuHome = makeMenuIcon(House);
export const MenuBrandDna = makeMenuIcon(Fingerprint);
export const MenuHistory = makeMenuIcon(History);
export const MenuRunway = makeMenuIcon(TrendingUp);
export const MenuSocial = makeMenuIcon(Share2);
export const MenuTrendHistory = makeMenuIcon(ChartNoAxesCombined);
export const MenuMoodboards = makeMenuIcon(Images);
export const MenuMyMoodboards = makeMenuIcon(LayoutGrid);
export const MenuRunwayMoodboards = makeMenuIcon(TrendingUp);
export const MenuBrandCatalogues = makeMenuIcon(BookOpen);
export const MenuDesign = makeMenuIcon(Palette);
export const MenuDesignCanvas = makeMenuIcon(PenTool);
export const MenuCollectionRangePlan = makeMenuIcon(Layers);
export const MenuFolder = makeMenuIcon(FolderOpen);
export const MenuDesignAssets = makeMenuIcon(PencilRuler);
export const MenuPhotoshootAssets = makeMenuIcon(GalleryHorizontalEnd);
export const MenuFabrics = makeMenuIcon(Shapes);
export const MenuPrints = makeMenuIcon(Grid2x2);
export const MenuOthers = makeMenuIcon(Image);
export const MenuSketches = makeMenuIcon(PenLine);
export const MenuImages = makeMenuIcon(Image);
// Design Assets > Color Palettes ("Colors") left-menu entry (REF-6361). Palette — the colour-mixing glyph.
export const MenuColorPalettes = makeMenuIcon(Palette);
// Design Assets > Brand Kits left-menu entry (REF-6155-brand-kits). SwatchBook — reads as a
// swatch/sample book of the kit's assets.
export const MenuBrandKits = makeMenuIcon(SwatchBook);
export const MenuPhotoshoot = makeMenuIcon(Aperture);
export const MenuPhotoshootStudio = makeMenuIcon(Camera);
export const MenuProducts = makeMenuIcon(Shirt);
export const MenuBackgrounds = makeMenuIcon(Mountain);
export const MenuPoses = makeMenuIcon(PersonStanding);
export { MenuModel };
export const MenuTechPack = makeMenuIcon(FileText);
export const MenuBulkProcess = makeMenuIcon(Sparkles);
export { MenuIntegrations };
